import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, DatePicker, TimePicker, InputNumber, message, Divider, Row, Col, Card, Popconfirm, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ClearOutlined, EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import { turmaService, unidadeService, modalidadeService, equipeService, alunoService, matriculaService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TurmasList = () => {
  const { user } = useAuth();
  const [turmas, setTurmas] = useState([]);
  const [turmasFiltradas, setTurmasFiltradas] = useState([]);
  const [matriculas, setMatriculas] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [alunosResponsavel, setAlunosResponsavel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMatriculaModalVisible, setIsMatriculaModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedTurma, setSelectedTurma] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [form] = Form.useForm();
  const [matriculaForm] = Form.useForm();
  const [filtros, setFiltros] = useState({
    id: '',
    nome_turma: '',
    busca_geral: '',
    id_unidades: null,
    id_modalidades: null,
    id_equipe: null,
  });

  useEffect(() => {
    fetchTurmas();
    fetchUnidades();
    fetchModalidades();
    fetchEquipes();
    fetchMatriculas();
    fetchAlunos();
    if (user?.role === 'RESPONSAVEL') {
      fetchAlunosResponsavel();
    }
  }, []);

  const aplicarFiltros = useCallback(() => {
    let resultado = [...turmas];

    if (filtros.id) {
      const termoId = String(filtros.id).trim();
      resultado = resultado.filter((t) => {
        const idTexto = String(t?.id ?? '');
        return idTexto === termoId || idTexto.includes(termoId);
      });
    }

    if (filtros.nome_turma) {
      const termoNomeTurma = String(filtros.nome_turma).toLowerCase().trim();
      resultado = resultado.filter((t) => {
        const nome = String(t?.nome_turm ?? '').toLowerCase();
        return nome.includes(termoNomeTurma);
      });
    }

    if (filtros.id_unidades) {
      resultado = resultado.filter(t => t.id_unidades === filtros.id_unidades);
    }

    if (filtros.id_modalidades) {
      resultado = resultado.filter(t => t.id_modalidades === filtros.id_modalidades);
    }

    if (filtros.id_equipe) {
      resultado = resultado.filter(t => t.id_equipe === filtros.id_equipe);
    }

    if (filtros.busca_geral) {
      const termo = filtros.busca_geral.toLowerCase();
      resultado = resultado.filter(t => {
        const nome = t.nome_turm ? t.nome_turm.toLowerCase() : '';
        const horario = t.turma_horario ? t.turma_horario.toLowerCase() : '';
        return nome.includes(termo) || horario.includes(termo);
      });
    }

    setTurmasFiltradas(resultado);
  }, [turmas, filtros]);

  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros({ id: '', nome_turma: '', busca_geral: '', id_unidades: null, id_modalidades: null, id_equipe: null });
    message.success('Filtros limpos');
  };

  const fetchTurmas = async () => {
    setLoading(true);
    try {
      const response = await turmaService.list();
      setTurmas(response.data);
    } catch (error) {
      message.error('Erro ao carregar turmas');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchUnidades = async () => {
    try {
      const response = await unidadeService.list();
      setUnidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades');
    }
  };

  const fetchModalidades = async () => {
    try {
      const response = await modalidadeService.list();
      setModalidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar modalidades');
    }
  };

  const fetchEquipes = async () => {
    try {
      const response = await equipeService.list();
      setEquipes(response.data);
    } catch (error) {
      console.error('Erro ao carregar equipes');
    }
  };

  const fetchAlunos = async () => {
    try {
      const response = await alunoService.list();
      setAlunos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar alunos');
    }
  };

  const fetchMatriculas = async () => {
    try {
      const response = await matriculaService.list();
      setMatriculas(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar matrículas');
    }
  };

  const fetchAlunosResponsavel = async () => {
    try {
      const response = await alunoService.list();
      setAlunosResponsavel(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar alunos do responsável');
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    
    // Parse dias_semana: pode vir como array, string JSON ou string separada por vírgula
    let diasSemana = [];
    if (record.dias_semana) {
      if (Array.isArray(record.dias_semana)) {
        diasSemana = record.dias_semana;
      } else if (typeof record.dias_semana === 'string') {
        try {
          // Tentar parse JSON
          diasSemana = JSON.parse(record.dias_semana);
        } catch {
          // Se falhar, pode ser string separada por vírgula
          diasSemana = record.dias_semana.split(',').map(d => d.trim());
        }
      }
    }
    
    const formData = {
      ...record,
      dias_semana: diasSemana,
      hora_inicio: record.hora_inicio ? dayjs(record.hora_inicio, 'HH:mm:ss') : null,
      hora_fim: record.hora_fim ? dayjs(record.hora_fim, 'HH:mm:ss') : null,
      data_inicio: record.data_inicio ? dayjs(record.data_inicio, 'YYYY-MM-DD') : null,
      data_fim: record.data_fim ? dayjs(record.data_fim, 'YYYY-MM-DD') : null,
    };
    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir esta turma?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await turmaService.delete(id);
          message.success('Turma excluída com sucesso');
          fetchTurmas();
        } catch (error) {
          message.error('Erro ao excluir turma');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (values.data_inicio && values.data_fim && values.data_inicio.isAfter(values.data_fim, 'day')) {
        message.error('Data de início não pode ser maior que data de fim');
        return;
      }

      const submitData = {
        ...values,
        hora_inicio: values.hora_inicio ? values.hora_inicio.format('HH:mm:ss') : null,
        hora_fim: values.hora_fim ? values.hora_fim.format('HH:mm:ss') : null,
        data_inicio: values.data_inicio ? values.data_inicio.format('YYYY-MM-DD') : null,
        data_fim: values.data_fim ? values.data_fim.format('YYYY-MM-DD') : null,
      };

      if (editingId) {
        await turmaService.update(editingId, submitData);
        message.success('Turma atualizada com sucesso');
      } else {
        await turmaService.create(submitData);
        message.success('Turma criada com sucesso');
      }
      setIsModalVisible(false);
      fetchTurmas();
    } catch (error) {
      message.error('Erro ao salvar turma');
      console.error(error);
    }
  };

  const handleSolicitarMatricula = (turma) => {
    setSelectedTurma(turma);
    matriculaForm.resetFields();
    matriculaForm.setFieldsValue({
      ano_letivo: dayjs(),
    });
    setIsMatriculaModalVisible(true);
  };

  const handleSubmitMatricula = async (values) => {
    if (!selectedTurma) return;
    try {
      const payload = {
        id_alunos: values.id_alunos,
        id_turma: selectedTurma.id,
        id_unidades: selectedTurma.id_unidades,
        ano_letivo: values.ano_letivo.format('YYYY'),
        data_inicio: values.data_inicio ? values.data_inicio.format('YYYY-MM-DD') : null,
      };

      await matriculaService.create(payload);
      message.success('Solicitação de matrícula enviada. Aguardando aprovação.');
      setIsMatriculaModalVisible(false);
    } catch (error) {
      const data = error?.response?.data || {};
      const detail = data?.detail;
      const userMessage =
        typeof data?.user_message === 'string'
          ? data.user_message
          : typeof detail === 'string'
            ? detail
            : detail?.user_message;
      message.error(userMessage || 'Erro ao solicitar matrícula');
    }
  };

  const getUnidadeName = (id_unidades) => {
    const unidade = unidades.find((u) => u.id === id_unidades);
    return unidade ? unidade.nome_fantasia : '-';
  };

  const getModalidadeName = (id_modalidades) => {
    const modalidade = modalidades.find((m) => m.id === id_modalidades);
    return modalidade ? modalidade.nome_modalidade : '-';
  };

  const getEquipeName = (id_equipe) => {
    const equipe = equipes.find((e) => e.id === id_equipe);
    return equipe ? equipe.nome : '-';
  };

  const formatDateDisplay = (value) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
  };

  const getAlunosMatriculados = (turmaId) => {
    const matriculasTurma = matriculas.filter((m) => {
      if (m.id_turma !== turmaId) return false;
      const status = (m.status || 'PENDENTE').toUpperCase().trim();
      if (status.startsWith('CANCEL')) return false;
      return status.startsWith('ATIV') || status.startsWith('PEND');
    });
    return matriculasTurma
      .map((m) => {
        const aluno = alunos.find((a) => a.id === m.id_alunos) || null;
        const alunoNome = aluno?.nome_aluno || 'Aluno';
        const status = (m.status || 'PENDENTE').toUpperCase().trim();
        return {
          id: m.id,
          aluno_id: m.id_alunos,
          nome_aluno: alunoNome,
          status,
        };
      })
      .sort((a, b) => (a.nome_aluno || '').localeCompare(b.nome_aluno || ''));
  };

  const handleViewTurma = (record) => {
    setSelectedTurma(record);
    setIsViewModalVisible(true);
  };

  const handleDownloadRelatorio = (record) => {
    try {
      const alunosTurma = getAlunosMatriculados(record.id);
      const doc = new jsPDF();

      doc.setFontSize(14);
      doc.text('Relatório da Turma', 14, 16);

      doc.setFontSize(10);
      doc.text(`Turma: ${record.nome_turm || '-'}`, 14, 24);
      doc.text(`Unidade: ${record.unidade_nome || getUnidadeName(record.id_unidades)}`, 14, 30);
      doc.text(`Modalidade: ${record.modalidade_nome || getModalidadeName(record.id_modalidades)}`, 14, 36);
      doc.text(`Professor: ${record.professor_nome || getEquipeName(record.id_equipe)}`, 14, 42);
      doc.text(`Horário: ${record.turma_horario || '-'}`, 14, 48);

      autoTable(doc, {
        startY: 56,
        head: [['Aluno', 'Status']],
        body: alunosTurma.map((a) => [a.nome_aluno, a.status || '-']),
        styles: { fontSize: 9 },
      });

      const nomeArquivo = `turma_${(record.nome_turm || 'turma').replace(/\s+/g, '_')}.pdf`;
      doc.save(nomeArquivo);
    } catch (error) {
      console.error('Erro ao gerar relatório de turma', error);
      message.error('Erro ao gerar relatório de turma');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0),
    },
    {
      title: 'Nome da Turma',
      dataIndex: 'nome_turm',
      key: 'nome_turm',
      sorter: (a, b) => (a.nome_turm || '').localeCompare(b.nome_turm || ''),
    },
    {
      title: 'Unidade',
      dataIndex: 'id_unidades',
      key: 'id_unidades',
      render: (id_unidades, record) => record?.unidade_nome || getUnidadeName(id_unidades),
      sorter: (a, b) => (a.unidade_nome || getUnidadeName(a.id_unidades)).localeCompare(b.unidade_nome || getUnidadeName(b.id_unidades)),
    },
    {
      title: 'Modalidade',
      dataIndex: 'id_modalidades',
      key: 'id_modalidades',
      render: (id_modalidades, record) => record?.modalidade_nome || getModalidadeName(id_modalidades),
      sorter: (a, b) => (a.modalidade_nome || getModalidadeName(a.id_modalidades)).localeCompare(b.modalidade_nome || getModalidadeName(b.id_modalidades)),
    },
    {
      title: 'Professor',
      dataIndex: 'id_equipe',
      key: 'id_equipe',
      render: (id_equipe, record) => record?.professor_nome || getEquipeName(id_equipe),
      sorter: (a, b) => (a.professor_nome || getEquipeName(a.id_equipe)).localeCompare(b.professor_nome || getEquipeName(b.id_equipe)),
    },
    {
      title: 'Dias da Semana',
      dataIndex: 'dias_semana',
      key: 'dias_semana',
      render: (dias) => {
        if (!dias) return '-';
        if (Array.isArray(dias)) return dias.join(', ');
        if (typeof dias === 'string') {
          try {
            const diasArray = JSON.parse(dias);
            return Array.isArray(diasArray) ? diasArray.join(', ') : dias;
          } catch {
            return dias;
          }
        }
        return '-';
      },
    },
    {
      title: 'Horário',
      dataIndex: 'turma_horario',
      key: 'turma_horario',
      sorter: (a, b) => (a.turma_horario || '').localeCompare(b.turma_horario || ''),
    },
    {
      title: 'Vagas',
      key: 'vagas',
      width: 100,
      render: (_, record) => {
        const total = record.total_alunos || 0;
        const maximo = record.maximo_alunos || 0;
        const percentual = maximo > 0 ? Math.round((total / maximo) * 100) : 0;
        const color = percentual >= 100 ? 'red' : percentual >= 80 ? 'orange' : 'green';
        return (
          <span style={{ color, fontWeight: 'bold' }}>
            {total}/{maximo}
          </span>
        );
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        if (user?.role === 'RESPONSAVEL') {
          return (
            <Space>
              <Button
                size="small"
                type="primary"
                onClick={() => handleSolicitarMatricula(record)}
              >
                Solicitar Matrícula
              </Button>
            </Space>
          );
        }

        return (
          <Space>
            <Button
              icon={<EyeOutlined />}
              size="small"
              title="Visualizar"
              onClick={() => handleViewTurma(record)}
            />
            <Button
              icon={<FilePdfOutlined />}
              size="small"
              title="Baixar relatório"
              onClick={() => handleDownloadRelatorio(record)}
            />
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
            <Popconfirm
              title="Recriar aulas desta turma?"
              description="Isso irá gerar aulas faltantes dentro do período da turma."
              onConfirm={async () => {
                try {
                  await turmaService.recalcularAulas(record.id, true);
                  message.success('Aulas recalculadas com sucesso');
                } catch (error) {
                  const detail = error?.response?.data?.detail;
                  const userMessage =
                    typeof detail === 'string'
                      ? detail
                      : detail?.user_message;
                  message.error(userMessage || 'Erro ao recalcular aulas');
                }
              }}
              okText="Recalcular"
              cancelText="Cancelar"
            >
              <Button
                size="small"
                title="Recalcular Aulas"
              >
                Recalcular
              </Button>
            </Popconfirm>
            {user?.role !== 'OFFICE' && (
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record.id)}
                size="small"
              />
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card
        title="Filtros de Busca"
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <div style={{ marginBottom: 8, fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
          ({turmasFiltradas.length} de {turmas.length} turmas)
        </div>
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>ID</div>
              <Input
                placeholder="Filtrar por ID"
                value={filtros.id}
                onChange={(e) => handleFiltroChange('id', String(e?.target?.value || '').replace(/\D/g, ''))}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Turma</div>
              <Input
                placeholder="Filtrar por nome da turma"
                value={filtros.nome_turma}
                onChange={(e) => handleFiltroChange('nome_turma', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
              <Input
                placeholder="Pesquisar por nome, horário..."
                prefix={<SearchOutlined />}
                value={filtros.busca_geral}
                onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Unidade</div>
              <Select
                placeholder="Todas as unidades"
                value={filtros.id_unidades}
                onChange={(value) => handleFiltroChange('id_unidades', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {unidades.map(u => (
                  <Select.Option key={u.id} value={u.id}>{u.nome_fantasia}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Modalidade</div>
              <Select
                placeholder="Todas as modalidades"
                value={filtros.id_modalidades}
                onChange={(value) => handleFiltroChange('id_modalidades', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {modalidades.map(m => (
                  <Select.Option key={m.id} value={m.id}>{m.nome_modalidade}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Professor</div>
              <Select
                placeholder="Todos os professores"
                value={filtros.id_equipe}
                onChange={(value) => handleFiltroChange('id_equipe', value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {equipes.map(e => (
                  <Select.Option key={e.id} value={e.id}>{e.nome}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      {user?.role !== 'RESPONSAVEL' && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Nova Turma
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={turmasFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} turmas`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        scroll={{ x: 1400, y: 520 }}
        sticky
      />

      <Modal
        title="Detalhes da Turma"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={null}
        width={900}
      >
        {selectedTurma && (
          <>
            <Divider>Dados da Turma</Divider>
            <Row gutter={[16, 8]}>
              <Col xs={24} md={12}><strong>Nome:</strong> {selectedTurma.nome_turm || '-'}</Col>
              <Col xs={24} md={12}><strong>Unidade:</strong> {selectedTurma.unidade_nome || getUnidadeName(selectedTurma.id_unidades)}</Col>
              <Col xs={24} md={12}><strong>Modalidade:</strong> {selectedTurma.modalidade_nome || getModalidadeName(selectedTurma.id_modalidades)}</Col>
              <Col xs={24} md={12}><strong>Professor:</strong> {selectedTurma.professor_nome || getEquipeName(selectedTurma.id_equipe)}</Col>
              <Col xs={24} md={12}><strong>Horário:</strong> {selectedTurma.turma_horario || '-'}</Col>
              <Col xs={24} md={12}><strong>Data Início:</strong> {formatDateDisplay(selectedTurma.data_inicio)}</Col>
              <Col xs={24} md={12}><strong>Data Fim:</strong> {formatDateDisplay(selectedTurma.data_fim)}</Col>
              <Col xs={24} md={12}><strong>Dias:</strong> {Array.isArray(selectedTurma.dias_semana)
                ? selectedTurma.dias_semana.join(', ')
                : (selectedTurma.dias_semana || '-')}
              </Col>
            </Row>

            <Divider>Alunos Matriculados</Divider>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={getAlunosMatriculados(selectedTurma.id)}
              columns={[
                { title: 'Aluno', dataIndex: 'nome_aluno', key: 'nome_aluno' },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  width: 140,
                  render: (status) => {
                    const color = status === 'ATIVA' ? 'green' : status === 'CANCELADA' ? 'red' : 'orange';
                    return <Tag color={color}>{status}</Tag>;
                  }
                },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        title={editingId ? 'Editar Turma' : 'Nova Turma'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Informações Básicas */}
          <Divider>Informações Básicas</Divider>

          <Form.Item
            name="nome_turm"
            label="Nome da Turma"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input placeholder="Ex: Turma A - Segunda" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_unidades"
                label="Unidade"
                rules={[{ required: true, message: 'Unidade é obrigatória' }]}
              >
                <Select placeholder="Selecione a unidade">
                  {unidades.map((unidade) => (
                    <Select.Option key={unidade.id} value={unidade.id}>
                      {unidade.nome_fantasia}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_modalidades"
                label="Modalidade"
                rules={[{ required: true, message: 'Modalidade é obrigatória' }]}
              >
                <Select placeholder="Selecione a modalidade">
                  {modalidades.map((modalidade) => (
                    <Select.Option key={modalidade.id} value={modalidade.id}>
                      {modalidade.nome_modalidade}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="id_equipe"
                label="Professor"
                rules={[{ required: true, message: 'Professor é obrigatório' }]}
              >
                <Select placeholder="Selecione o professor">
                  {equipes.map((equipe) => (
                    <Select.Option key={equipe.id} value={equipe.id}>
                      {equipe.nome}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="turma_horario" label="Horário (Descrição)">
                <Input placeholder="Ex: 14:00 - 15:30" />
              </Form.Item>
            </Col>
          </Row>

          {/* Horário */}
          <Divider>Horário e Dias da Semana</Divider>

          <Form.Item 
            name="dias_semana" 
            label="Dias da Semana"
            rules={[{ required: true, message: 'Selecione ao menos um dia' }]}
          >
            <Select mode="multiple" placeholder="Selecione os dias da semana">
              <Select.Option value="SEGUNDA">Segunda-feira</Select.Option>
              <Select.Option value="TERCA">Terça-feira</Select.Option>
              <Select.Option value="QUARTA">Quarta-feira</Select.Option>
              <Select.Option value="QUINTA">Quinta-feira</Select.Option>
              <Select.Option value="SEXTA">Sexta-feira</Select.Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="hora_inicio" label="Hora de Início">
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="hora_fim" label="Hora de Fim">
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Período da Turma</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="data_inicio"
                label="Data de Início"
                rules={[{ required: true, message: 'Data de início é obrigatória' }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="data_fim"
                label="Data de Fim"
                dependencies={['data_inicio']}
                rules={[
                  { required: true, message: 'Data de fim é obrigatória' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const inicio = getFieldValue('data_inicio');
                      if (!inicio || !value || !value.isBefore(inicio, 'day')) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Data de fim deve ser igual ou maior que data de início'));
                    },
                  }),
                ]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Capacidade */}
          <Divider>Capacidade da Turma</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="minimo_alunos" 
                label="Mínimo de Alunos"
                rules={[{ required: true, message: 'Informe o mínimo de alunos' }]}
              >
                <InputNumber placeholder="5" min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="maximo_alunos" 
                label="Máximo de Alunos"
                rules={[{ required: true, message: 'Informe o máximo de alunos' }]}
              >
                <InputNumber placeholder="30" min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Solicitar Matrícula"
        open={isMatriculaModalVisible}
        onOk={() => matriculaForm.submit()}
        onCancel={() => setIsMatriculaModalVisible(false)}
        width={520}
      >
        <Form form={matriculaForm} layout="vertical" onFinish={handleSubmitMatricula}>
          <Form.Item
            name="id_alunos"
            label="Aluno"
            rules={[{ required: true, message: 'Selecione o aluno' }]}
          >
            <Select
              placeholder="Selecione o aluno"
              showSearch
              optionFilterProp="children"
            >
              {alunosResponsavel.map((aluno) => (
                <Select.Option key={aluno.id} value={aluno.id}>
                  {aluno.nome_aluno}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ano_letivo"
            label="Ano Letivo"
            rules={[{ required: true, message: 'Informe o ano letivo' }]}
          >
            <DatePicker picker="year" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="data_inicio" label="Data de Início">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TurmasList;
